import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchChangelog } from "@/services/changelog.service";
import type { ChangelogEntry } from "@/types/changelog";

const SEEN_KEY = "spidra:changelog:lastSeenAt";

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode / blocked storage — non-fatal */
  }
}

/**
 * Loads the changelog feed (marketing site, cross-origin, cached) and tracks a
 * single localStorage timestamp for "last time this browser opened it". No
 * server, no DB — per-browser unread is the norm for a what's-new surface.
 *
 * The dashboard announces the latest entry once (WhatsNewAnnouncer); opening or
 * dismissing it calls markAllSeen, so it only comes back for a newer release.
 */
export function useWhatsNew() {
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [lastSeenAt, setLastSeenAt] = useState<string | null>(null);

  useEffect(() => {
    setLastSeenAt(read(SEEN_KEY));
    let alive = true;
    fetchChangelog().then((e) => {
      if (!alive) return;
      setEntries(e);
      setLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  const latest = entries[0] ?? null;
  const seenTime = lastSeenAt ? new Date(lastSeenAt).getTime() : 0;

  const unreadCount = useMemo(
    () => entries.filter((e) => (e.date ? new Date(e.date).getTime() : 0) > seenTime).length,
    [entries, seenTime]
  );
  const hasUnread = loaded && unreadCount > 0;

  const markAllSeen = useCallback(() => {
    const now = new Date().toISOString();
    write(SEEN_KEY, now);
    setLastSeenAt(now);
  }, []);

  return { entries, loaded, latest, lastSeenAt, hasUnread, unreadCount, markAllSeen };
}
